import { designDestination } from '@/utils/designContent';
import { createRouter, createWebHistory } from "vue-router";
import {
  adminGuard,
  authGuard,
  getAuthContext,
  isPublicRoute,
  maintenanceGuard,
  verifyAdminBypass,
} from '@/app/router/guards';
import { blockNavigationIfDirty } from '@/composables/useUnsavedChanges';

const Home = () => import("@/pages/HomePage.vue");
const ProductDetails = () => import("@/pages/ProductDetailPage.vue");
const ProductSearch = () => import("@/pages/CatalogPage.vue");
const ShoppingCart = () => import("@/pages/CartPage.vue");
const CartPaymentWait = () => import("@/pages/CartPaymentWaitPage.vue");
const AdminProductFormPage = () => import("@/pages/admin/AdminProductFormPage.vue");
const LoginForm = () => import("@/pages/auth/LoginPage.vue");
const RegisterForm = () => import("@/pages/auth/RegisterPage.vue");
const EmailVerificationPage = () => import("@/pages/auth/EmailVerificationPage.vue");
const PasswordResetPage = () => import("@/pages/auth/PasswordResetPage.vue");
const AdminLayoutPage = () => import("@/pages/admin/AdminLayoutPage.vue");
const AdminCategoriesPage = () => import("@/pages/admin/AdminCategoriesPage.vue");
const AdminCreateCustomerPage = () => import("@/pages/admin/AdminCreateCustomerPage.vue");
const MaintenancePage = () => import("@/pages/MaintenancePage.vue");
const TermsAndConditions = () => import("@/pages/TermsPage.vue");
const PrivacyPolicy = () => import("@/pages/PrivacyPage.vue");
const RefundsPolicy = () => import("@/pages/RefundsPage.vue");
const ShippingPolicy = () => import("@/pages/ShippingPage.vue");
const ContactPage = () => import("@/pages/ContactPage.vue");
const AccountLayoutPage = () => import("@/pages/account/AccountLayoutPage.vue");
const NotFoundPage = () => import("@/pages/NotFoundPage.vue");

import { resolveRouteTitle, setDocumentTitle } from '@/utils/siteTitle';

const redirectToAccount = (path) => (to) => ({
  path,
  query: to.query,
  hash: to.hash,
});

const routes = [ 
  {
    path: "/mantenimiento",
    name: "Maintenance",
    component: MaintenancePage,
    meta: {
      allowDuringMaintenance: true,
      title: 'Sitio en Mantenimiento'
    }
  },
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: { title: 'Inicio', titleKey: 'page.home' }
  },
  {
    path: "/account",
    redirect: "/account/orders",
  },
  {
    path: "/account/orders",
    component: AccountLayoutPage,
    meta: { requiresAuth: true, title: 'Mis Pedidos', titleKey: 'account.myOrders' },
    children: [
      {
        path: "",
        component: () => import('@/pages/account/AccountOrdersPage.vue'),
        meta: { requiresAuth: true, title: 'Mis Pedidos', titleKey: 'account.myOrders' }
      },
    ],
  },
  {
    path: "/account/settings",
    component: AccountLayoutPage,
    meta: { requiresAuth: true, title: 'Mi Cuenta', titleKey: 'account.myAccount' },
    children: [
      {
        path: "",
        component: () => import('@/pages/account/AccountSettingsPage.vue'),
        meta: { requiresAuth: true, title: 'Mi Cuenta', titleKey: 'account.myAccount' }
      },
    ],
  },
  {
    path: "/account/contact",
    component: AccountLayoutPage,
    meta: { requiresAuth: true, title: 'Contacto', titleKey: 'page.contact' },
    children: [
      {
        path: "",
        component: () => import('@/pages/account/AccountContactPage.vue'),
        meta: { requiresAuth: true, title: 'Contacto', titleKey: 'page.contact' }
      },
    ],
  },
  {
    path: "/account/addresses",
    component: AccountLayoutPage,
    meta: { requiresAuth: true, title: 'Direcciones de Envío', titleKey: 'account.addresses' },
    children: [
      {
        path: "",
        component: () => import('@/pages/account/AccountAddressesPage.vue'),
        meta: { requiresAuth: true, title: 'Direcciones de Envío', titleKey: 'account.addresses' }
      },
    ],
  },
  {
    path: "/configuracion/my-orders",
    redirect: redirectToAccount('/account/orders'),
  },
  {
    path: "/configuracion/mi-cuenta",
    redirect: redirectToAccount('/account/settings'),
  },
  {
    path: "/configuracion/contacto",
    redirect: redirectToAccount('/account/contact'),
  },
  {
    path: "/configuracion/direcciones",
    redirect: redirectToAccount('/account/addresses'),
  },

  {
    path: "/configuracion",
    component: AdminLayoutPage,
    meta: { requiresAuth: true, title: 'Configuración' },
    children: [
      {
        path: "",
        name: "UserDashboard",
        component: () => import('@/pages/account/AccountDashboardPage.vue'),
        meta: { requiresAuth: true, title: 'Mi Panel', titleKey: 'account.dashboard' }
      },
      {
        path: "admin",
        name: "AdminOverview",
        component: () => import('@/pages/admin/AdminDashboardPage.vue'),
        meta: { requiresAdmin: true, title: 'Panel de Administración' }
      },
      {
        path: "envios/gls",
        name: "AdminGlsConfig",
        component: () => import('@/pages/admin/AdminGlsConfigPage.vue'),
        meta: { requiresAdmin: true, title: 'Envíos — GLS' }
      },
      {
        path: "envios/costos",
        name: "AdminProvinceShipping",
        component: () => import('@/pages/admin/AdminProvinceShippingPage.vue'),
        meta: { requiresAdmin: true, title: 'Costos de envíos' }
      },
      {
        path: "ventas",
        name: "AdminSalesHistory",
        component: () => import('@/pages/admin/AdminSalesPage.vue'),
        meta: { 
          requiresAdmin: true,
          title: 'Historial de Ventas',
          // Añadimos esta propiedad para forzar que el componente siempre se vuelva a cargar
          keepAlive: false 
        }
      },
      {
        path: "ventas/carritos-abandonados",
        name: "AdminVentasCarritos",
        component: () => import('@/pages/admin/AdminAbandonedCartsPage.vue'),
        meta: { requiresAdmin: true, title: 'Carritos pendientes y abandonados' }
      },
      {
        path: "solicitudes",
        name: "AdminOrderRequests",
        component: () => import('@/pages/admin/AdminOrderRequestsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Solicitudes de cancelación y devolución'
        }
      },
      {
        path: "stats",
        name: "AdminStats",
        component: () => import('@/pages/admin/AdminStatsPage.vue'),
        meta: { requiresAdmin: true, title: 'Estadísticas' }
      },
      {
        path: "ventas/nueva",
        name: "admin-new-sale",
        component: () => import('@/pages/admin/AdminNewSalePage.vue'),
        meta: { requiresAdmin: true, title: 'Nueva Venta' }
      },
      {
        path: "productos",
        name: "AdminProductList",
        component: () => import('@/pages/admin/AdminProductsPage.vue'),
        meta: { requiresAdmin: true, title: 'Gestión de Productos' }
      },
      {
        path: "productos/agregar",
        name: "AddProduct",
        component: AdminProductFormPage,
        meta: { requiresAdmin: true, title: 'Agregar Producto' }
      },
      {
        path: "productos/editar/:productId",
        name: "ProductEdit",
        component: AdminProductFormPage,
        props: true,  
        meta: { requiresAdmin: true, title: 'Editar Producto' }
      },
      {
        path: "productos/tallas",
        name: "AdminSizeCharts",
        component: () => import('@/pages/admin/AdminSizeChartsPage.vue'),
        meta: { requiresAdmin: true, title: 'Tablas de tallas' }
      },
      {
        path: "orders",
        redirect: "/account/orders",
      },
      {
        path: "settings",
        redirect: "/account/settings",
      },
      {
        path: "addresses",
        redirect: "/account/addresses",
        meta: { requiresAuth: true, title: 'Direcciones de Envío' }
      },
      {
        path: "categorias",
        name: "admin-categories",
        component: AdminCategoriesPage,
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Categorías'
        }
      },
      {
        path: "clientes",
        name: "admin-customers",
        component: () => import('@/pages/admin/AdminCustomersPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Clientes'
        }
      },
      {
        path: '/configuracion/clientes',
        name: 'customers',
        component: () => import('@/pages/admin/AdminCustomersPage.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Clientes' }
      },
      {
        path: '/configuracion/clientes/crear',
        name: 'create-customer',
        component: AdminCreateCustomerPage,
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Crear Cliente' }
      },
      {
        path: 'distribuidores-pendientes',
        name: 'admin-distributor-pending',
        component: () => import('@/pages/admin/AdminDistributorPendingPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Solicitudes distribuidor'
        }
      },
      {
        path: '/configuracion/clientes/:id',
        name: 'customer-profile',
        component: () => import('@/pages/admin/AdminCustomerProfilePage.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Perfil de Cliente' }
      },
      {
        path: '/configuracion/pedidos/:id',
        name: 'order-details',
        component: () => import('@/pages/admin/AdminOrderDetailPage.vue'),
        meta: { requiresAuth: true, title: 'Detalle de Pedido' }
      },
      {
        path: 'coupons',
        name: 'AdminCoupons',
        component: () => import('@/pages/admin/AdminCouponsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Cupones'
        }
      },
      {
        path: 'offers',
        name: 'AdminOffers',
        component: () => import('@/pages/admin/AdminOffersPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Ofertas'
        }
      },
      {
        path: 'scheduled-discounts',
        name: 'AdminScheduledDiscounts',
        component: () => import('@/pages/admin/AdminScheduledDiscountsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Descuentos Programados'
        }
      },      {
        path: "mi-tienda",
        name: "StoreSettings",
        component: () => import('@/pages/admin/AdminStoreSettingsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Mi Tienda'
        }
      },
      {
        path: "remito",
        name: "AdminRemitoSettings",
        component: () => import('@/pages/admin/AdminRemitoSettingsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Remitos'
        }
      },
      {
        path: "legal",
        name: "AdminLegalContent",
        redirect: to => ({ path: '/configuracion/diseno', query: { ...to.query, tab: 'legal' } }),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Contenido legal'
        }
      },
      {
        path: "idiomas",
        name: "AdminLanguages",
        redirect: to => designDestination(to.query),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Idiomas y traducciones'
        }
      },
      {
        path: "diseno",
        name: "AdminHomeDesign",
        component: () => import('@/pages/admin/AdminHomeDesignPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Diseno del Home'
        }
      },
      {
        path: "empleados",
        name: "AdminEmployees",
        component: () => import('@/pages/admin/AdminEmployeesPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Gestión de Empleados'
        }
      },
      {
        path: "roles",
        name: "AdminRoles",
        component: () => import('@/pages/admin/AdminRolesPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Roles y Permisos'
        }
      },
    ]
  },
  {
    // Solo enteros positivos: evita que rutas tipo /producto/filter-facets disparen la vista de detalle.
    path: "/producto/:id(\\d+)",
    name: "ProductDetails",
    component: ProductDetails,
    meta: { title: 'Producto', titleKey: 'page.product', embeddedFooter: true }
  },
  {
    path: "/productos",
    name: "Products",
    component: ProductSearch,
    meta: { title: 'Productos', titleKey: 'page.products', embeddedFooter: true }
  },
  {
    path: "/terminos-y-condiciones",
    name: "TermsAndConditions",
    component: TermsAndConditions,
    meta: { title: 'Términos y Condiciones', titleKey: 'page.terms', embeddedFooter: true }
  },
  {
    path: "/politica-de-privacidad",
    name: "PrivacyPolicy",
    component: PrivacyPolicy,
    meta: { title: 'Política de Privacidad', titleKey: 'page.privacy', embeddedFooter: true }
  },
  {
    path: "/politica-de-reembolsos",
    name: "RefundsPolicy",
    component: RefundsPolicy,
    meta: { title: 'Política de Reembolsos', titleKey: 'page.refunds', embeddedFooter: true }
  },
  {
    path: "/envios",
    name: "ShippingPolicy",
    component: ShippingPolicy,
    meta: { title: 'Envíos', titleKey: 'page.shipping', embeddedFooter: true }
  },
  {
    path: "/contacto",
    name: "Contact",
    component: ContactPage,
    meta: { title: 'Contacto', titleKey: 'page.contact', embeddedFooter: true }
  },
  {
    path: "/preguntas-frecuentes",
    redirect: { path: '/contacto', hash: '#faq' },
  },
  {
    path: "/carrito/",
    name: "ShoppingCart",
    component: ShoppingCart,
    meta: { title: 'Carrito de Compras', titleKey: 'page.cart', embeddedFooter: true }
  },
  {
    path: "/carrito/paymentwait",
    name: "CartPaymentWait",
    component: CartPaymentWait,
    meta: { title: 'Preparando pago', titleKey: 'page.paymentWait', embeddedFooter: true }
  },
  {
    path: "/cart",
    redirect: "/carrito/"
  },
  {
    path: "/iniciar-sesion",
    name: "LoginForm",
    component: LoginForm,
    meta: { redirectIfAuth: true, title: 'Iniciar Sesión', titleKey: 'page.login' }
  },
  {
    path: "/crear-cuenta",
    name: "RegisterForm",
    component: RegisterForm,
    meta: { redirectIfAuth: true, title: 'Crear Cuenta', titleKey: 'page.register' }
  },
  {
    path: "/verificar-email",
    name: "EmailVerification",
    component: EmailVerificationPage,
    meta: { title: 'Verificar Email', titleKey: 'page.verifyEmail' }
  },
  {
    path: "/recuperar-contrasena",
    name: "PasswordReset",
    component: PasswordResetPage,
    meta: { redirectIfAuth: true, title: 'Recuperar Contraseña', titleKey: 'page.recoverPassword' }
  },
  {
    path: '/payment/result',
    name: 'PaymentResult',
    redirect: (to) => ({
      path: '/account/orders',
      query: { ...to.query, payment_return: to.query.payment_return || 'mercadopago' },
    })
  },
  {
    path: '/payment/paypal/return',
    name: 'PayPalPaymentReturn',
    redirect: (to) => ({
      path: '/account/orders',
      query: { ...to.query, payment_return: 'paypal' },
    })
  },
  {
    path: '/payment/stripe/return',
    name: 'StripePaymentReturn',
    redirect: (to) => ({
      path: '/account/orders',
      query: { ...to.query, payment_return: 'stripe' },
    })
  },
  {
    path: '/configuracion/ventas/:id/imprimir',
    name: 'PrintOrder',
    component: () => import('@/pages/admin/AdminPrintOrderPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Imprimir Pedido' }
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFoundPage,
    meta: { title: 'Página no encontrada' }
  }
];

const scrollPageToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        el: to.hash,
        top: 80,
        behavior: 'auto',
      };
    }

    return { left: 0, top: 0 };
  },
});

  // SOLUCIÓN CORREGIDA PARA ROUTER GUARD - PRIORIDAD ABSOLUTA PARA ADMINS
router.beforeEach(async (to, _from, next) => {
  // Cambios sin guardar en el panel: bloquear la navegación y preguntar
  // antes de salir (el modal resuelve y re-navega al destino).
  if (to.path !== _from.path && blockNavigationIfDirty(to)) {
    return next(false);
  }

  if (isPublicRoute(to)) {
    return next();
  }

  const authContext = getAuthContext();

  if (await verifyAdminBypass(authContext)) {
    return next();
  }

  const maintenanceRedirect = await maintenanceGuard(to);
  if (maintenanceRedirect) {
    return next(maintenanceRedirect);
  }

  const authRedirect = authGuard(to, authContext);
  if (authRedirect) {
    return next(authRedirect);
  }

  const adminRedirect = adminGuard(to, authContext);
  if (adminRedirect) {
    return next(adminRedirect);
  }

  next();
});
// Actualizar document.title y forzar scroll al inicio en cada navegación
router.afterEach((to) => {
  setDocumentTitle(resolveRouteTitle(to.meta));

  if (to.hash) return;

  // Refuerzo tras redirects, rutas anidadas y componentes async
  requestAnimationFrame(() => {
    scrollPageToTop();
  });
});

export default router;
